import {ActionIcon, Button, Group, MultiSelect, Stack, Text, TextInput, Title} from '@mantine/core'
import {useDebouncedValue} from '@mantine/hooks'
import {IconSearch, IconX} from '@tabler/icons-react'
import {Link, useNavigate} from '@tanstack/react-router'
import {DataTable, type DataTableColumn, type DataTableSortStatus} from 'mantine-datatable'
import {useMemo, useState} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'
import {useCustomers} from 'src/customers/use-customers'
import {formatPrice} from 'src/lib/format-price'

import {QuoteStatusBadge} from './quote-status-badge'
import type {QuoteListItem, QuoteStatus} from './schemas'
import {useQuotes} from './use-quotes'

const STATUS_FILTER_OPTIONS: Array<{value: QuoteStatus; label: string}> = [
    {value: 'draft', label: 'Draft'},
    {value: 'sent', label: 'Sent'},
    {value: 'accepted', label: 'Accepted'},
    {value: 'rejected', label: 'Rejected'},
    {value: 'voided', label: 'Voided'},
]

function formatCreatedAt(createdAt: string) {
    return formatBusinessTimestamp(createdAt, 'MMM d, yyyy')
}

function columnFilterClearButton(value: string, onClear: () => void) {
    if (!value) {
        return null
    }

    return (
        <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={onClear}>
            <IconX size={14} />
        </ActionIcon>
    )
}

function compareQuotes(
    left: QuoteListItem,
    right: QuoteListItem,
    columnAccessor: DataTableSortStatus<QuoteListItem>['columnAccessor'],
) {
    if (columnAccessor === 'total') {
        return left.total - right.total
    }

    if (columnAccessor === 'lineItemCount') {
        return left.lineItemCount - right.lineItemCount
    }

    if (columnAccessor === 'createdAt') {
        return left.createdAt.localeCompare(right.createdAt)
    }

    const leftValue = String(left[columnAccessor as keyof QuoteListItem] ?? '')
    const rightValue = String(right[columnAccessor as keyof QuoteListItem] ?? '')
    return leftValue.localeCompare(rightValue)
}

function sortQuotes(quotes: QuoteListItem[], sortStatus: DataTableSortStatus<QuoteListItem>) {
    const directionMultiplier = sortStatus.direction === 'asc' ? 1 : -1
    return [...quotes].sort(
        (left, right) =>
            directionMultiplier * compareQuotes(left, right, sortStatus.columnAccessor),
    )
}

export function QuotesPage() {
    const navigate = useNavigate()
    const {data: quotes = [], isLoading, isError, error} = useQuotes()
    const {data: customers = []} = useCustomers()

    const customerNameById = useMemo(
        () => new Map(customers.map((customer) => [customer.id, customer.name])),
        [customers],
    )

    const [nameFilter, setNameFilter] = useState('')
    const [customerFilter, setCustomerFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState<QuoteStatus[]>([])
    const [debouncedName] = useDebouncedValue(nameFilter, 300)
    const [debouncedCustomer] = useDebouncedValue(customerFilter, 300)
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<QuoteListItem>>({
        columnAccessor: 'createdAt',
        direction: 'desc',
    })

    const filtersActive = Boolean(
        debouncedName.trim() || debouncedCustomer.trim() || statusFilter.length > 0,
    )

    const filteredQuotes = useMemo(() => {
        const normalizedName = debouncedName.trim().toLowerCase()
        const normalizedCustomer = debouncedCustomer.trim().toLowerCase()

        return quotes.filter((quote) => {
            if (normalizedName && !(quote.name ?? '').toLowerCase().includes(normalizedName)) {
                return false
            }

            const customerName = customerNameById.get(quote.customerId) ?? ''
            if (normalizedCustomer && !customerName.toLowerCase().includes(normalizedCustomer)) {
                return false
            }

            if (statusFilter.length > 0 && !statusFilter.includes(quote.status)) {
                return false
            }

            return true
        })
    }, [customerNameById, debouncedCustomer, debouncedName, quotes, statusFilter])

    const sortedQuotes = useMemo(
        () => sortQuotes(filteredQuotes, sortStatus),
        [filteredQuotes, sortStatus],
    )

    const columns = useMemo((): DataTableColumn<QuoteListItem>[] => {
        return [
            {
                accessor: 'customerId',
                title: 'Customer',
                sortable: true,
                render: ({customerId}) => customerNameById.get(customerId) ?? '',
                filter: (
                    <TextInput
                        label="Customer"
                        description="Show quotes for customers whose names include the specified text"
                        placeholder="Search customer names..."
                        leftSection={<IconSearch size={16} stroke={1.5} />}
                        rightSection={columnFilterClearButton(customerFilter, () =>
                            setCustomerFilter(''),
                        )}
                        value={customerFilter}
                        onChange={(event) => setCustomerFilter(event.currentTarget.value)}
                    />
                ),
                filtering: customerFilter !== '',
            },
            {
                accessor: 'status',
                title: 'Status',
                sortable: true,
                width: 130,
                render: ({status}) => <QuoteStatusBadge status={status} />,
                filter: (
                    <Stack w={200}>
                        <MultiSelect
                            label="Status"
                            description="Show quotes in any of the selected statuses"
                            placeholder="All statuses"
                            data={STATUS_FILTER_OPTIONS}
                            value={statusFilter}
                            onChange={setStatusFilter}
                            clearable
                        />
                    </Stack>
                ),
                filtering: statusFilter.length > 0,
            },
            {
                accessor: 'name',
                title: 'Name',
                sortable: true,
                render: ({name}) => name ?? '',
                filter: (
                    <TextInput
                        label="Name"
                        description="Show quotes whose names include the specified text"
                        placeholder="Search quote names..."
                        leftSection={<IconSearch size={16} stroke={1.5} />}
                        rightSection={columnFilterClearButton(nameFilter, () => setNameFilter(''))}
                        value={nameFilter}
                        onChange={(event) => setNameFilter(event.currentTarget.value)}
                    />
                ),
                filtering: nameFilter !== '',
            },
            {
                accessor: 'createdByName',
                title: 'Rep',
                sortable: true,
                render: ({createdByName}) => createdByName ?? '',
            },
            {
                accessor: 'lineItemCount',
                title: 'Line items',
                sortable: true,
                width: 100,
            },
            {
                accessor: 'total',
                title: 'Total',
                sortable: true,
                render: ({total}) => formatPrice(total),
            },
            {
                accessor: 'createdAt',
                title: 'Created',
                sortable: true,
                render: ({createdAt}) => formatCreatedAt(createdAt),
            },
        ]
    }, [customerFilter, customerNameById, nameFilter, statusFilter])

    if (isError) {
        return (
            <Stack gap="md">
                <Title order={1}>Quotes</Title>
                <Text c="red">
                    {error instanceof Error ? error.message : 'Failed to load quotes'}
                </Text>
            </Stack>
        )
    }

    return (
        <Stack gap="md">
            <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                    <Title order={1}>Quotes</Title>
                    <Text size="sm" c="dimmed">
                        Saved sales quotes with line items and totals.
                    </Text>
                </Stack>
                <Button component={Link} to="/quotes/new">
                    Create quote
                </Button>
            </Group>

            <DataTable
                withTableBorder
                withColumnBorders
                striped
                highlightOnHover
                textSelectionDisabled
                records={sortedQuotes}
                columns={columns}
                idAccessor="id"
                fetching={isLoading}
                minHeight={sortedQuotes.length === 0 ? 180 : undefined}
                noRecordsText={filtersActive ? 'No quotes match your filters.' : 'No quotes yet.'}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                onRowClick={({record}) =>
                    void navigate({
                        to: '/quotes/$quoteId',
                        params: {quoteId: record.id},
                    })
                }
            />
        </Stack>
    )
}
